import React, { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, Image, Video, ChevronDown, X, Upload } from 'lucide-react';
import api from '../../services/api';

const MESES = [
  'Janeiro','Fevereiro','Março','Abril','Maio','Junho',
  'Julho','Agosto','Setembro','Outubro','Novembro','Dezembro',
];

function mesLabel(dateStr) {
  if (!dateStr) return '';
  const [y, m] = dateStr.split('-');
  return `${MESES[parseInt(m, 10) - 1]} de ${y}`;
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' });
}

function mesAtual() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
}

export default function EvolucaoObra() {
  const qc = useQueryClient();
  const fileInputRef = useRef();

  const [empSelecionado, setEmpSelecionado] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ titulo: '', texto: '', mes_referencia: mesAtual(), percentual: '' });
  const [arquivos, setArquivos] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [deletandoId, setDeletandoId] = useState(null);

  const { data: empreendimentos = [] } = useQuery({
    queryKey: ['empreendimentos'],
    queryFn: () => api.get('/empreendimentos').then(r => r.data.data || r.data),
  });

  const { data: atualizacoes = [], isLoading } = useQuery({
    queryKey: ['evolucao-obra', empSelecionado],
    queryFn: () => api.get('/evolucao-obra', { params: { empreendimento: empSelecionado } }).then(r => r.data.data),
    enabled: !!empSelecionado,
  });

  const criar = useMutation({
    mutationFn: (fd) => api.post('/evolucao-obra', fd, { headers: { 'Content-Type': 'multipart/form-data' } }),
    onSuccess: () => {
      qc.invalidateQueries(['evolucao-obra', empSelecionado]);
      setShowForm(false);
      setForm({ titulo: '', texto: '', mes_referencia: mesAtual(), percentual: '' });
      setArquivos([]);
      setPreviews([]);
    },
  });

  const deletar = useMutation({
    mutationFn: (id) => api.delete(`/evolucao-obra/${id}`),
    onSuccess: () => qc.invalidateQueries(['evolucao-obra', empSelecionado]),
  });

  const handleFiles = (e) => {
    const files = Array.from(e.target.files);
    setArquivos(prev => [...prev, ...files]);
    const newPreviews = files.map(f => ({
      name: f.name,
      tipo: f.type.startsWith('video/') ? 'video' : 'foto',
      url: URL.createObjectURL(f),
    }));
    setPreviews(prev => [...prev, ...newPreviews]);
  };

  const removeArquivo = (i) => {
    setArquivos(prev => prev.filter((_, idx) => idx !== i));
    setPreviews(prev => prev.filter((_, idx) => idx !== i));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!empSelecionado) return;
    const fd = new FormData();
    fd.append('empreendimento_nome', empSelecionado);
    fd.append('titulo', form.titulo);
    fd.append('texto', form.texto);
    fd.append('mes_referencia', form.mes_referencia);
    fd.append('percentual', form.percentual || 0);
    arquivos.forEach(f => fd.append('midias', f));
    criar.mutate(fd);
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Cabeçalho */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Evolução das Obras</h1>
          <p className="text-gray-500 text-sm mt-1">Publique atualizações mensais visíveis no portal do cliente</p>
        </div>
        {empSelecionado && (
          <button
            onClick={() => setShowForm(v => !v)}
            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-blue-700 transition-colors"
          >
            <Plus size={16} />
            Nova atualização
          </button>
        )}
      </div>

      {/* Seletor de empreendimento */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <label className="block text-sm font-semibold text-gray-700 mb-2">Empreendimento</label>
        <div className="relative">
          <select
            value={empSelecionado}
            onChange={e => { setEmpSelecionado(e.target.value); setShowForm(false); }}
            className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm appearance-none focus:outline-none focus:ring-2 focus:ring-blue-500 pr-8"
          >
            <option value="">Selecione um empreendimento...</option>
            {empreendimentos.map(e => (
              <option key={e.id} value={e.nome}>{e.nome}</option>
            ))}
          </select>
          <ChevronDown size={16} className="absolute right-3 top-3 text-gray-400 pointer-events-none" />
        </div>
      </div>

      {/* Formulário nova atualização */}
      {showForm && (
        <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-blue-200 shadow-sm p-6 space-y-4">
          <h2 className="font-semibold text-gray-800 text-lg">Nova atualização — {empSelecionado}</h2>

          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Título *</label>
              <input
                required
                value={form.titulo}
                onChange={e => setForm(f => ({ ...f, titulo: e.target.value }))}
                placeholder="Ex: Concretagem do 5º andar concluída"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Mês de referência *</label>
              <input
                type="month"
                required
                value={form.mes_referencia.slice(0, 7)}
                onChange={e => setForm(f => ({ ...f, mes_referencia: e.target.value + '-01' }))}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">% de conclusão da obra</label>
              <input
                type="number" min="0" max="100" step="0.5"
                value={form.percentual}
                onChange={e => setForm(f => ({ ...f, percentual: e.target.value }))}
                placeholder="Ex: 42"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Texto da atualização</label>
              <textarea
                rows={5}
                value={form.texto}
                onChange={e => setForm(f => ({ ...f, texto: e.target.value }))}
                placeholder="Descreva o que foi realizado na obra durante este mês..."
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              />
            </div>
          </div>

          {/* Upload de mídias */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Fotos e vídeos</label>
            <div
              onClick={() => fileInputRef.current?.click()}
              className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors"
            >
              <Upload size={28} className="mx-auto text-gray-400 mb-2" />
              <p className="text-sm text-gray-500">Clique para selecionar fotos e vídeos</p>
              <p className="text-xs text-gray-400 mt-1">JPG, PNG, MP4, MOV — até 200MB por arquivo</p>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*,video/*"
              className="hidden"
              onChange={handleFiles}
            />

            {previews.length > 0 && (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 mt-3">
                {previews.map((p, i) => (
                  <div key={i} className="relative group aspect-square bg-gray-100 rounded-lg overflow-hidden">
                    {p.tipo === 'foto' ? (
                      <img src={p.url} alt={p.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex flex-col items-center justify-center gap-1 bg-gray-800">
                        <Video size={24} className="text-white" />
                        <span className="text-white text-xs text-center px-1 truncate w-full text-center">{p.name}</span>
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => removeArquivo(i)}
                      className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X size={12} />
                    </button>
                    <div className="absolute bottom-1 left-1">
                      <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${p.tipo === 'video' ? 'bg-blue-600 text-white' : 'bg-green-600 text-white'}`}>
                        {p.tipo === 'video' ? '▶ vídeo' : '📷 foto'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="submit"
              disabled={criar.isPending}
              className="flex-1 bg-blue-600 text-white py-2.5 rounded-lg font-semibold text-sm hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {criar.isPending ? 'Publicando...' : 'Publicar atualização'}
            </button>
            <button
              type="button"
              onClick={() => setShowForm(false)}
              className="px-4 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50"
            >
              Cancelar
            </button>
          </div>
        </form>
      )}

      {/* Lista de atualizações */}
      {empSelecionado && (
        <div className="space-y-4">
          {isLoading ? (
            <div className="text-center py-12 text-gray-400">Carregando...</div>
          ) : atualizacoes.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
              <div className="text-4xl mb-3">🏗️</div>
              <p className="text-gray-500 font-medium">Nenhuma atualização publicada ainda</p>
              <p className="text-gray-400 text-sm mt-1">Clique em "Nova atualização" para começar</p>
            </div>
          ) : (
            atualizacoes.map(a => (
              <div key={a.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
                {/* Header do card */}
                <div className="px-6 py-4 flex items-start justify-between border-b border-gray-100">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold bg-blue-100 text-blue-700 px-2.5 py-1 rounded-full">
                        {mesLabel(a.mes_referencia)}
                      </span>
                      {a.percentual > 0 && (
                        <span className="text-xs font-semibold bg-green-100 text-green-700 px-2.5 py-1 rounded-full">
                          {a.percentual}% concluído
                        </span>
                      )}
                    </div>
                    <h3 className="font-bold text-gray-900 mt-2 text-base">{a.titulo}</h3>
                    <p className="text-xs text-gray-400 mt-0.5">Publicado em {formatDate(a.created_at)}</p>
                  </div>
                  <button
                    onClick={() => {
                      if (deletandoId === a.id) {
                        deletar.mutate(a.id);
                        setDeletandoId(null);
                      } else {
                        setDeletandoId(a.id);
                      }
                    }}
                    className={`p-2 rounded-lg transition-colors ${deletandoId === a.id ? 'bg-red-100 text-red-600' : 'text-gray-400 hover:text-red-500 hover:bg-red-50'}`}
                    title={deletandoId === a.id ? 'Clique novamente para confirmar' : 'Excluir atualização'}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>

                {/* Texto */}
                {a.texto && (
                  <div className="px-6 py-4">
                    <p className="text-gray-700 text-sm leading-relaxed whitespace-pre-line">{a.texto}</p>
                  </div>
                )}

                {/* Mídias */}
                {a.midias?.length > 0 && (
                  <div className="px-6 pb-5">
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {a.midias.map(m => (
                        <div key={m.id} className="aspect-square bg-gray-100 rounded-lg overflow-hidden">
                          {m.tipo === 'foto' ? (
                            <img
                              src={`${process.env.REACT_APP_API_URL?.replace('/api', '')}${m.url}`}
                              alt={m.legenda || ''}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <video
                              src={`${process.env.REACT_APP_API_URL?.replace('/api', '')}${m.url}`}
                              controls
                              className="w-full h-full object-cover"
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
